import React, { useState } from 'react';
import { itemFields } from '../shared/connectorContext';
import { reasonLabel } from './dataConnectors';

const formatDate = (iso) => {
  const t = Date.parse(iso || '');
  return Number.isFinite(t)
    ? new Date(t).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';
};

/** Column labels in order of first appearance, so every item lines up in one table. */
const columnsOf = (rows) => {
  const seen = [];
  rows.forEach((fields) => fields.forEach((f) => {
    if (f.label && !seen.includes(f.label)) seen.push(f.label);
  }));
  return seen;
};

const asLink = (value) => (/^https?:\/\/\S+$/i.test(value) ? value : null);

/**
 * Custom researcher results for the Intelligence view, one tab per data source
 * (data: researchIntelligence.connectors.{id}).
 */
export default function ConnectorDataInsight({ connectors }) {
  const records = Object.entries(connectors || {})
    .filter(([, r]) => r && typeof r === 'object')
    .map(([id, r]) => ({ id, ...r }))
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  const [activeId, setActiveId] = useState(null);
  if (!records.length) return null;

  const active = records.find((r) => r.id === activeId) || records[0];
  const rows = (Array.isArray(active.items) ? active.items : []).map(itemFields).filter((f) => f.length);
  const columns = columnsOf(rows);
  const total = Number(active.total) || 0;
  const fetched = formatDate(active.fetchedAt);

  return (
    <section className="ir-section ir-dc">
      <div className="ir-section-label">
        <i className="fa fa-plug" />External Data
        <span className="ir-section-count">{records.length}</span>
      </div>

      <div className="ir-dc-card">
        <div className="ir-dc-tabs" role="tablist">
          {records.map((r) => {
            const n = Number(r.total) || 0;
            const failed = r.reason && r.reason !== 'no_match';
            return (
              <button
                key={r.id}
                type="button"
                role="tab"
                aria-selected={r.id === active.id}
                className={`ir-dc-tab${r.id === active.id ? ' ir-dc-tab--active' : ''}`}
                onClick={() => setActiveId(r.id)}
              >
                <span className="ir-dc-tab-name">{r.name || 'Custom Researcher'}</span>
                <span className={`ir-dc-tab-count${n ? '' : ' ir-dc-tab-count--empty'}`}>
                  {failed ? <i className="fa fa-triangle-exclamation" /> : n}
                </span>
              </button>
            );
          })}
        </div>

        <div className="ir-dc-panel" role="tabpanel">
          <div className="ir-dc-meta">
            {fetched && <span><i className="fa fa-clock" />Fetched {fetched}</span>}
            <span>
              <i className="fa fa-list-ol" />
              {total} result{total === 1 ? '' : 's'}{total > rows.length && rows.length ? ` · showing ${rows.length}` : ''}
            </span>
            <span className={active.useInEmails === false ? 'ir-dc-off' : 'ir-dc-on'}>
              <i className={`fa ${active.useInEmails === false ? 'fa-eye-slash' : 'fa-envelope-open-text'}`} />
              {active.useInEmails === false ? 'Not used in emails' : 'Used in emails'}
            </span>
          </div>

          {Array.isArray(active.columns) && active.columns.length > 0 && (
            <ul className="ir-dc-columns">
              {active.columns.map((c) => (
                <li key={c.key}><b>{c.label || c.key}</b> {c.value}</li>
              ))}
            </ul>
          )}

          {rows.length ? (
            <div className="ir-dc-table-wrap">
              <table className="ir-dc-table">
                <thead>
                  <tr>
                    <th className="ir-dc-num">#</th>
                    {columns.map((c) => <th key={c}>{c}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((fields, i) => (
                    <tr key={i}>
                      <td className="ir-dc-num">{i + 1}</td>
                      {columns.map((c) => {
                        const value = String(fields.find((f) => f.label === c)?.value ?? '');
                        const href = asLink(value);
                        return (
                          <td key={c}>
                            {href
                              ? <a href={href} target="_blank" rel="noopener noreferrer">{value.replace(/^https?:\/\//, '')}</a>
                              : value || <span className="ir-dc-blank">–</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="ir-dc-empty">
              <i className="fa fa-circle-minus" />
              <span>{reasonLabel(active.reason || 'no_match')}</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
